import _ from 'lodash'
import { createSelector } from 'reselect'
import { STABLE_EMPTY_ARRAY } from '@feedyou/utils'
import {
  BotDefaultLocale,
  Campaign,
  CampaignId,
  IntentId,
  NlpIntent,
  NlpModel,
  NlpModelId,
  StepId,
  TelephonyChannelSchema
} from '@feedyou-ai/feedbot-schema'

import { RootState } from '../../../reducers'
import { Model } from '../../../components/nlp/typesOld'
import { extractStepsFromDialogs } from '../../../lib/dialogs'
import { botFlowSelector } from './botFlowSelector'
import { dialogsSelector } from './dialogsSelector'
import { isOneOfTypes } from '../../../lib/step'
import { stepTypeDefaultsSelector } from './stepTypeDefaultsSelector'
import { isModelTypeDescriptionBased } from '../../../features/nlpModel'
import { getTelephonyChannel } from '../../../lib/bot/getTelephonyChannel'

export const nlpModelsSelector = createSelector(
  (s: RootState) => botFlowSelector(s)?.nlp?.models,
  models => models ?? STABLE_EMPTY_ARRAY
)

export const botMutationsSelector = createSelector(
  (s: RootState) => botFlowSelector(s)?.mutations,
  mutations => mutations ?? STABLE_EMPTY_ARRAY
)

export const getBotMutationsSelector = (state: RootState) => {
  return botFlowSelector(state)?.mutations ?? STABLE_EMPTY_ARRAY
}

export const doesBotHaveMutationsSelector = createSelector(
  getBotMutationsSelector,
  mutations => !!mutations.length
)

export function getBotDefaultLocaleSelector(state: RootState): BotDefaultLocale | undefined {
  return botFlowSelector(state)?.defaultLocale
}

export const nlpModelByIntentIdSelector = (intentId?: string) => (state: RootState) => {
  const models = nlpModelsSelector(state)
  return models.find(model => model?.intents?.some(intent => intent?.id === intentId))
}

export const stepByIntentIdSelector =
  (intentId?: string) =>
  (state: RootState): Model | undefined => {
    // This selector is used for step-level NLP
    const steps = allStepsSelector(state)
    const questionLikeStep = steps.filter(isOneOfTypes(['prompt', 'question']))
    return questionLikeStep?.find(step => step?.answers?.some(answer => answer.id === intentId))
  }

export const nlpIntentSelector =
  (id?: IntentId) =>
  (state: RootState): NlpIntent | undefined => {
    const parentModel = nlpModelByIntentIdSelector(id)(state)
    return parentModel?.intents?.find(intent => intent.id === id)
  }

export const telephonyChannelSelector = createSelector(botFlowSelector, flow => {
  return getTelephonyChannel(flow)
})

export const isTelephonySelector = createSelector(
  telephonyChannelSelector,
  (telephonyChannel: TelephonyChannelSchema | undefined): boolean => {
    return !!telephonyChannel
  }
)

export const telephonyVoiceSelector = createSelector(
  telephonyChannelSelector,
  (telephonyChannel: TelephonyChannelSchema | undefined): string => {
    return telephonyChannel?.textToSpeech?.voice || ''
  }
)

export const campaignSelector = (campaignId: CampaignId) => {
  return createSelector(
    telephonyChannelSelector,
    (telephonyChannel: TelephonyChannelSchema | undefined): Campaign | undefined => {
      return telephonyChannel?.campaigns?.find(({ id }) => id === campaignId)
    }
  )
}

export const allStepsSelector = createSelector(
  dialogsSelector, // I <3 PRETTIER
  dialogs => extractStepsFromDialogs(Object.values(dialogs))
)

export const stepSelector = _.memoize((id: StepId) =>
  createSelector(
    allStepsSelector, // I <3 PRETTIER
    allSteps => allSteps.find(step => step.id === id)
  )
)

export const nlpSelector = createSelector(
  (s: RootState) => botFlowSelector(s)?.nlp,
  nlp => nlp
)

export const telephonySpeechRateSelector = createSelector(
  botFlowSelector, // I <3 PRETTIER
  (flow): number | undefined => {
    return flow?.channels?.find(channel => channel.type === 'telephony')?.textToSpeech?.speechRate
  }
)

export const defaultQuestionGrammarsSelector = (state: RootState) => {
  return stepTypeDefaultsSelector(state)?.question?.grammars ?? STABLE_EMPTY_ARRAY
}

export const isModelDescriptionBasedSelector = (modelId: NlpModelId) =>
  createSelector(nlpModelsSelector, (nlpModels: NlpModel[]): boolean | undefined => {
    const nlpModelProviderType = nlpModels.find(model => model.id === modelId)?.provider?.type
    return nlpModelProviderType && isModelTypeDescriptionBased(nlpModelProviderType)
  })

export const didStatsigInitFailSelector = (state: RootState): boolean => {
  return state.features.didStatsigInitFail
}
